// Clean console on app load/reload (fresh start for debugging)
console.clear();

import React from 'react';
import ReactDOM from 'react-dom/client';
import AppShell from './AppShell.tsx';
import { ThemeProvider } from './contexts/ThemeContext';
import { ContentProvider } from './contexts/ContentContext';
import { initLogger } from './utils/logger';
import { authManager } from './utils/authManager';
import logger from './utils/simpleLogger';
import './index.css';

// Initialize frontend logger
logger.info('📝 Inicializando logger...');
initLogger();
logger.info('✅ Logger inicializado');

// Función async para inicializar la app completa
async function initializeApp() {
  try {
    logger.info('🚀 Iniciando aplicación...');
    logger.info('📍 Root element:', document.getElementById('root'));

    // Mostrar loading mientras inicializamos
    const rootElement = document.getElementById('root');
    if (!rootElement) {
      throw new Error('No se encontró el elemento #root en el DOM');
    }
    logger.info('✅ Root element encontrado, mostrando loading...');
    // NOTA: Este HTML se renderiza antes de React, por lo que usamos valores que coinciden con design-system/tokens/colors.ts
    // Light mode: bg=#fafafa, text=#171717, text-secondary=#525252, accent=#2563eb, border=#d4d4d4
    rootElement.innerHTML = `
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100dvh; font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #fafafa;">
        <div style="text-align: center;">
          <div style="width: 48px; height: 48px; border: 4px solid #d4d4d4; border-top-color: #2563eb; border-radius: 50%; margin: 0 auto 16px; animation: spin 1s linear infinite;"></div>
          <h2 style="color: #171717; margin: 0 0 8px 0; font-size: 20px; font-weight: 600;">Cargando MyChat</h2>
          <p style="color: #525252; margin: 0; font-size: 14px;">Iniciando aplicación...</p>
        </div>
        <style>
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        </style>
      </div>
    `;

    // CRÍTICO: Esperar a que authManager se inicialice COMPLETAMENTE antes de renderizar
    await authManager.initialize();
    logger.info('🔐 AuthManager inicializado correctamente');

    // Verificar que tenemos un token válido
    const token = authManager.getToken();
    const user = authManager.getUser();
    logger.info('✅ Token disponible:', token ? 'Sí' : 'No');
    logger.info('✅ Usuario disponible:', user ? user.username : 'No');

    // Service Worker: Always unregister in development (localhost)
    // Only register in production (non-localhost domains)
    if ('serviceWorker' in navigator) {
      const hostname = window.location.hostname;
      const isLocalhost =
        hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.');

      if (isLocalhost) {
        // Development: Always unregister existing service workers
        logger.info('🔧 Modo desarrollo detectado, desregistrando Service Workers...');
        navigator.serviceWorker
          .getRegistrations()
          .then(async (registrations) => {
            if (registrations.length > 0) {
              logger.info(`🚫 Desregistrando ${registrations.length} Service Worker(s)`);
              await Promise.all(registrations.map((registration) => registration.unregister()));
            }
          })
          .then(() => {
            return caches.keys();
          })
          .then(async (cacheNames) => {
            if (cacheNames.length > 0) {
              logger.info(`🗑️ Limpiando ${cacheNames.length} cache(s)`);
              await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
            }
          })
          .then(() => {
            logger.info('✅ Service Workers y caches limpiados para desarrollo');
          })
          .catch((error) => {
            console.warn('⚠️ Error limpiando Service Worker:', error);
          });
      } else {
        // Production: register service worker
        navigator.serviceWorker
          .register('/sw.js')
          .then((registration) => {
            logger.info('✅ SW registered for production');
          })
          .catch((registrationError) => {
            logger.info('❌ SW registration failed: ', registrationError);
          });
      }
    }

    // AHORA sí renderizar la app (con authManager ya inicializado)
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <ThemeProvider>
          <ContentProvider>
            <AppShell />
          </ContentProvider>
        </ThemeProvider>
      </React.StrictMode>
    );

    logger.info('✅ Aplicación renderizada exitosamente');
  } catch (error) {
    console.error('❌ Error inicializando aplicación:', error);
    console.error('❌ Stack trace:', error instanceof Error ? error.stack : 'No stack available');
    // Mostrar error en pantalla
    const root = document.getElementById('root');
    if (root) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      root.innerHTML =
        '<div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100dvh; font-family: sans-serif;"><h1 style="color: red;">Error al inicializar la aplicación</h1><p>' +
        errorMessage +
        '</p><button onclick="location.reload()" style="padding: 10px 20px; margin-top: 20px; cursor: pointer;">Reintentar</button></div>';
    }
  }
}

// Ejecutar inicialización
initializeApp();
